/* eslint-disable lingui/no-unlocalized-strings -- API identifiers. */
import type { AnalyticsContextDto } from "@reactive-resume/dto";
import axios from "axios";

import { getClerkToken } from "@/client/libs/clerk-token";

import { getFirstTouch, getLatestCampaign } from "./attribution";
import { analyticsAllowed } from "./consent";
import { isTrackedEnvironment } from "./environment";
import { getGa4Ids } from "./ga4";

export const getAnalyticsContext = async (): Promise<AnalyticsContextDto | undefined> => {
  if (!isTrackedEnvironment()) return undefined;
  if (!analyticsAllowed()) return { consent: "denied" };
  const ids = await getGa4Ids();
  if (!analyticsAllowed()) return { consent: "denied" };
  return {
    consent: "granted",
    ...ids,
    firstTouch: getFirstTouch() ?? undefined,
    latestCampaign: getLatestCampaign() ?? undefined,
  };
};

/** Quiet and bounded: analytics failure must never prevent signup, checkout or an interview. */
const syncOnce = async () => {
  try {
    let context = await getAnalyticsContext();
    if (!context) return;
    const token = await getClerkToken();
    if (!token) return;
    if (!analyticsAllowed()) context = { consent: "denied" };
    await axios.post("/api/analytics/context", context, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 3000,
    });
  } catch {
    // Retry on navigation, foregrounding, and before checkout.
  }
};

let pending: Promise<void> | undefined;
let requested = false;
export const syncAnalyticsContext = (): Promise<void> => {
  requested = true;
  pending ??= (async () => {
    while (requested) {
      requested = false;
      await syncOnce();
    }
  })().finally(() => {
    pending = undefined;
  });
  return pending;
};
